import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Banknote,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/lib/auth';
import {
  useReceitas,
  useReceitasAggregate,
  useDeleteReceita,
  type Receita,
} from '@/lib/hooks/receitas';
import { ReceitaDrawer } from '@/features/receitas/ReceitaDrawer';
import { fmtBRL, fmtDate } from '@/lib/utils';

export const Route = createFileRoute('/_app/lancamentos/receitas')({
  component: ReceitasPage,
});

function currentCompetencia(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MES_NOMES: Record<string, string> = {
  '01': 'janeiro', '02': 'fevereiro', '03': 'março', '04': 'abril',
  '05': 'maio', '06': 'junho', '07': 'julho', '08': 'agosto',
  '09': 'setembro', '10': 'outubro', '11': 'novembro', '12': 'dezembro',
};

const PAGE_SIZE = 25;

function ReceitasPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [competencia, setCompetencia] = useState(currentCompetencia());
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Receita | null>(null);

  const filters = useMemo(
    () => ({
      competencia,
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [competencia, search, offset],
  );

  const listQ = useReceitas(workspaceId, filters);
  const aggQ = useReceitasAggregate(workspaceId, competencia);
  const delMut = useDeleteReceita(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const mesNome = competencia ? MES_NOMES[competencia.slice(5, 7)] ?? '' : '';

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Receita removida.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para gerenciar receitas.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lançamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Receitas <span className="text-fg-muted font-normal">· {mesNome}</span>
          </h1>
          <p className="text-sm text-fg-muted mt-2">Recursos recebidos pela entidade no mês</p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Nova receita
        </Button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <KPI
          tone="mint"
          label="Total recebido"
          value={fmtBRL(aggQ.data?.total ?? '0')}
          delta={`${aggQ.data?.count ?? 0} ${(aggQ.data?.count ?? 0) === 1 ? 'lançamento' : 'lançamentos'}`}
          icon={Banknote}
        />
        <KPI
          label="Competência"
          value={mesNome ? mesNome.charAt(0).toUpperCase() + mesNome.slice(1) : '—'}
          delta={competencia}
        />
        <KPI
          tone="ink"
          label="Última atualização"
          value={items[0] ? fmtDate(items[0].dataRecebimento) : '—'}
          delta={items[0]?.fonte ?? 'sem entradas'}
        />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted" strokeWidth={1.8} />
            <Input
              placeholder="Buscar por fonte…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
          <Input
            type="month"
            value={competencia}
            onChange={(e) => {
              setCompetencia(e.target.value);
              setOffset(0);
            }}
            className="w-[160px]"
          />
        </div>
      </Card>

      <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card-stroke bg-card-soft/60">
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Data</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Fonte</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Descrição</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Valor</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-fg-muted">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                    Carregando…
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-fg-muted text-sm">
                    Nenhuma receita encontrada neste mês.
                  </td>
                </tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40">
                  <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">{fmtDate(r.dataRecebimento)}</td>
                  <td className="px-4 py-3 text-sm font-medium text-fg">{r.fonte}</td>
                  <td className="px-4 py-3 text-sm text-fg-muted truncate max-w-[300px]">{r.descricao ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-fg tabular-nums">{fmtBRL(r.valor)}</td>
                  <td className="px-4 py-3 text-right relative">
                    <button
                      type="button"
                      onClick={() => setOpenRow(openRow === r.id ? null : r.id)}
                      className="grid h-8 w-8 ml-auto place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                      aria-label="Ações"
                    >
                      <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                    {openRow === r.id && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setOpenRow(null)} aria-hidden />
                        <div className="absolute right-2 top-10 z-40 w-40 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                          <button
                            type="button"
                            onClick={() => {
                              setOpenRow(null);
                              setConfirmDelete(r);
                            }}
                            className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                            Excluir
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
            <p className="text-xs text-fg-muted">
              Página <span className="text-fg font-medium">{currentPage}</span> de{' '}
              <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
              {total === 1 ? 'lançamento' : 'lançamentos'}
            </p>
            <div className="flex items-center gap-1">
              <Button type="button" variant="ghost" size="sm" disabled={!canPrev} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
              </Button>
              <Button type="button" variant="ghost" size="sm" disabled={!canNext} onClick={() => setOffset(offset + PAGE_SIZE)}>
                <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ReceitaDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        defaultCompetencia={competencia}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir esta receita?"
        description={
          confirmDelete
            ? `${confirmDelete.fonte} · ${fmtBRL(confirmDelete.valor)}. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
